import { Link } from "react-router-dom";
import { FadeUp, SlideInLeft, SlideInRight, StaggerContainer, ScaleIn } from "@/components/base/ScrollAnimations";
import { services, companyInfo, products } from "@/mocks/websiteData";

export default function Services() {
  return (
    <div className="min-h-screen pt-20 md:pt-24">
      {/* Hero */}
      <section className="relative h-[300px] md:h-[420px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Solar%20installation%20team%20working%20on%20large%20rooftop%20solar%20panel%20project%20in%20Indian%20village%2C%20multiple%20workers%20in%20safety%20gear%2C%20green%20fields%20in%20background%2C%20clear%20blue%20sky%2C%20professional%20photography%2C%20warm%20natural%20lighting&width=1600&height=700&seq=services-village-01&orientation=landscape"
          alt="Solar installation team in village"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/85"></div>
        <div className="relative z-10 section-padding text-center w-full">
          <FadeUp>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-500/25 backdrop-blur-sm border border-accent-400/30 mb-4">
              <span className="text-sm font-medium text-accent-300">7 Services · Solar · Drones · EV · All of Telangana</span>
            </div>
          </FadeUp>
          <FadeUp>
            <p className="text-sm font-medium text-background-100/80 mb-2 tracking-wide">NexSunNexova Solar Energy Pvt. Ltd.</p>
          </FadeUp>
          <FadeUp delay={100}>
            <h1 className="font-heading font-bold text-3xl md:text-5xl text-background-50 mb-3">Our Services</h1>
          </FadeUp>
          <FadeUp delay={200}>
            <p className="text-base md:text-lg text-background-200 max-w-2xl mx-auto">End-to-end solar energy, agricultural drone spraying, and EV charging solutions tailored for every need — from village homes to city offices.</p>
          </FadeUp>
        </div>
      </section>

      {/* Featured Core Services */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="section-padding">
          <FadeUp>
            <div className="text-center mb-10 md:mb-14">
              <span className="text-sm font-semibold text-accent-600 uppercase tracking-wider">Featured Services</span>
              <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-900 mt-2">Our Three Core Solutions</h2>
              <p className="text-foreground-600 mt-3 max-w-2xl mx-auto">These are our primary sales offerings — designed, supplied, installed, and maintained by our expert team.</p>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
            {/* Solar - On-Grid */}
            <FadeUp delay={0}>
              <div className="flex flex-col bg-background-100 rounded-2xl border border-background-200/70 overflow-hidden hover:border-primary-300/50 transition-all hover:-translate-y-1 shadow-sm hover:shadow-md">
                <div className="h-52 md:h-56 overflow-hidden shrink-0">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20residential%20rooftop%20solar%20panel%20installation%20on%20Indian%20home%20with%20clear%20sky%2C%20bright%20sunlight%2C%20clean%20professional%20photography%2C%20warm%20natural%20tones&width=800&height=500&seq=service-solar-featured-01&orientation=landscape&nocache=true"
                    alt="Solar Energy Installation"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-4">
                    <i className="ri-sun-line w-6 h-6 flex items-center justify-center"></i>
                  </div>
                  <h3 className="font-heading font-bold text-xl text-foreground-900 mb-2">Solar Energy Solutions</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">Complete solar EPC services from 3 kW home rooftops to 150 kW commercial plants. On-grid, off-grid, and hybrid systems with subsidy assistance.</p>
                  <ul className="space-y-2 mb-6">
                    <li className="flex items-center gap-2 text-sm text-foreground-600">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-accent-500 shrink-0"></i>
                      <span>EPC pricing from ₹66,000/kW</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-foreground-600">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-accent-500 shrink-0"></i>
                      <span>Subsidy up to ₹78,000</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-foreground-600">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-accent-500 shrink-0"></i>
                      <span>25-year warranty + AMC</span>
                    </li>
                  </ul>
                  <div className="mt-auto">
                    <Link to="/products" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all shadow-md shadow-primary-500/20 whitespace-nowrap">
                      View Solar Pricing
                      <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </FadeUp>

            {/* Drone Spray */}
            <FadeUp delay={100}>
              <div className="flex flex-col bg-background-100 rounded-2xl border border-background-200/70 overflow-hidden hover:border-accent-300/50 transition-all hover:-translate-y-1 shadow-sm hover:shadow-md">
                <div className="h-52 md:h-56 overflow-hidden shrink-0">
                  <img
                    src="https://readdy.ai/api/search-image?query=Agricultural%20drone%20spraying%20liquid%20fertilizer%20over%20lush%20green%20paddy%20field%20in%20India%2C%20visible%20mist%20droplets%20in%20golden%20sunlight%2C%20professional%20photography%2C%20high%20detail&width=800&height=500&seq=service-drone-featured-01&orientation=landscape&nocache=true"
                    alt="Agricultural Spray Drones"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-4">
                    <i className="ri-flight-takeoff-line w-6 h-6 flex items-center justify-center"></i>
                  </div>
                  <h3 className="font-heading font-bold text-xl text-foreground-900 mb-2">Agricultural Drone Spray</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">Precision crop spraying with 10L to 20L capacity drones. Cover 5 acres in 30 minutes. 30% less chemical use with targeted liquid application.</p>
                  <ul className="space-y-2 mb-6">
                    <li className="flex items-center gap-2 text-sm text-foreground-600">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-accent-500 shrink-0"></i>
                      <span>Drones from ₹1,85,000</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-foreground-600">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-accent-500 shrink-0"></i>
                      <span>Training + spare parts included</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-foreground-600">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-accent-500 shrink-0"></i>
                      <span>AMC packages available</span>
                    </li>
                  </ul>
                  <div className="mt-auto">
                    <Link to="/products" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-all shadow-md shadow-accent-500/20 whitespace-nowrap">
                      View Drone Pricing
                      <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </FadeUp>

            {/* EV Charging */}
            <FadeUp delay={200}>
              <div className="flex flex-col bg-background-100 rounded-2xl border border-background-200/70 overflow-hidden hover:border-secondary-300/50 transition-all hover:-translate-y-1 shadow-sm hover:shadow-md">
                <div className="h-52 md:h-56 overflow-hidden shrink-0">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20EV%20charging%20station%20unit%20in%20sleek%20parking%20lot%2C%20multiple%20charger%20posts%20with%20LED%20status%20indicators%2C%20electric%20car%20nearby%2C%20clean%20professional%20product%20photography%2C%20bright%20daylight%2C%20high%20tech%20futuristic%20design&width=800&height=500&seq=service-ev-featured-01&orientation=landscape&nocache=true"
                    alt="EV Charging Stations"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600 mb-4">
                    <i className="ri-charging-pile-line w-6 h-6 flex items-center justify-center"></i>
                  </div>
                  <h3 className="font-heading font-bold text-xl text-foreground-900 mb-2">EV Charging Stations</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">AC home chargers to DC fast charging infrastructure. 3.3 kW residential units to 120 kW commercial DC fast chargers with OCPP smart management.</p>
                  <ul className="space-y-2 mb-6">
                    <li className="flex items-center gap-2 text-sm text-foreground-600">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-accent-500 shrink-0"></i>
                      <span>AC home from ₹19,150 | DC fast up to 480 kW (GST incl.)</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-foreground-600">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-accent-500 shrink-0"></i>
                      <span>3.3 kW to 480 kW full range available</span>
                    </li>
                    <li className="flex items-center gap-2 text-sm text-foreground-600">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-accent-500 shrink-0"></i>
                      <span>Installation + subsidy support</span>
                    </li>
                  </ul>
                  <div className="mt-auto">
                    <Link to="/products" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-secondary-500 text-background-50 text-sm font-semibold hover:bg-secondary-600 transition-all shadow-md shadow-secondary-500/20 whitespace-nowrap">
                      View EV Pricing
                      <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* DJT Haika EV Charger Price List */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="section-padding">
          <FadeUp>
            <div className="text-center mb-8 md:mb-10">
              <span className="text-sm font-semibold text-accent-600 uppercase tracking-wider">EV Charging</span>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900 mt-2">DJT Haika EV Charger Rates</h2>
              <p className="text-foreground-600 mt-2 max-w-2xl mx-auto text-sm md:text-base">
                Complete price breakdown for all DJT Haika AC and DC chargers. Includes machine price, packing, electrical/EHT panel, and total with GST.
              </p>
            </div>
          </FadeUp>

          <FadeUp delay={100}>
            <div className="max-w-5xl mx-auto overflow-x-auto">
              <table className="w-full text-sm border border-background-200 rounded-xl overflow-hidden">
                <thead>
                  <tr className="bg-secondary-500 text-background-50">
                    <th className="text-left px-4 md:px-6 py-3 font-semibold">Power Rating</th>
                    <th className="text-right px-4 md:px-6 py-3 font-semibold">Machine Price<br/><span className="text-xs font-normal opacity-80">(incl. GST @ 5%)</span></th>
                    <th className="text-right px-4 md:px-6 py-3 font-semibold">Packing</th>
                    <th className="text-right px-4 md:px-6 py-3 font-semibold">Electrical / EHT Panel<br/><span className="text-xs font-normal opacity-80">(incl. GST @ 18%)</span></th>
                    <th className="text-right px-4 md:px-6 py-3 font-semibold">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-background-200 bg-background-50 hover:bg-background-100 transition-colors">
                    <td className="px-4 md:px-6 py-3 font-semibold text-foreground-900">3.3 kW AC</td>
                    <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹18,900</td>
                    <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹250</td>
                    <td className="px-4 md:px-6 py-3 text-right text-foreground-400">—</td>
                    <td className="px-4 md:px-6 py-3 text-right font-bold text-primary-600">₹19,150</td>
                  </tr>
                  <tr className="border-t border-background-200 hover:bg-background-100 transition-colors">
                    <td className="px-4 md:px-6 py-3 font-semibold text-foreground-900">7.4 kW AC</td>
                    <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹40,005</td>
                    <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹500</td>
                    <td className="px-4 md:px-6 py-3 text-right text-foreground-400">—</td>
                    <td className="px-4 md:px-6 py-3 text-right font-bold text-primary-600">₹40,505</td>
                  </tr>
                  <tr className="border-t border-background-200 bg-background-50 hover:bg-background-100 transition-colors">
                    <td className="px-4 md:px-6 py-3 font-semibold text-foreground-900">12 kW DC</td>
                    <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹3,04,500</td>
                    <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹6,000</td>
                    <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹20,000</td>
                    <td className="px-4 md:px-6 py-3 text-right font-bold text-primary-600">₹3,30,500</td>
                  </tr>
                  <tr className="border-t border-background-200 hover:bg-background-100 transition-colors">
                    <td className="px-4 md:px-6 py-3 font-semibold text-foreground-900">60 kW DC</td>
                    <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹9,97,500</td>
                    <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹18,000</td>
                    <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹1,15,640</td>
                    <td className="px-4 md:px-6 py-3 text-right font-bold text-primary-600">₹11,31,140</td>
                  </tr>
                  <tr className="border-t border-background-200 bg-background-50 hover:bg-background-100 transition-colors">
                    <td className="px-4 md:px-6 py-3 font-semibold text-foreground-900">120 kW DC</td>
                    <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹15,07,800</td>
                    <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹25,000</td>
                    <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹1,15,640</td>
                    <td className="px-4 md:px-6 py-3 text-right font-bold text-primary-600">₹16,48,440</td>
                  </tr>
                  <tr className="border-t border-background-200 hover:bg-background-100 transition-colors">
                    <td className="px-4 md:px-6 py-3 font-semibold text-foreground-900">180 kW DC</td>
                    <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹18,06,000</td>
                    <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹25,000</td>
                    <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹1,15,640</td>
                    <td className="px-4 md:px-6 py-3 text-right font-bold text-primary-600">₹19,46,640</td>
                  </tr>
                  <tr className="border-t border-background-200 bg-background-50 hover:bg-background-100 transition-colors">
                    <td className="px-4 md:px-6 py-3 font-semibold text-foreground-900">240 kW DC</td>
                    <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹20,80,050</td>
                    <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹25,000</td>
                    <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹1,51,040</td>
                    <td className="px-4 md:px-6 py-3 text-right font-bold text-primary-600">₹22,56,090</td>
                  </tr>
                  <tr className="border-t border-background-200 hover:bg-background-100 transition-colors">
                    <td className="px-4 md:px-6 py-3 font-semibold text-foreground-900">360 kW DC</td>
                    <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹33,66,300</td>
                    <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹25,000</td>
                    <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹1,51,040</td>
                    <td className="px-4 md:px-6 py-3 text-right font-bold text-primary-600">₹35,42,340</td>
                  </tr>
                  <tr className="border-t border-background-200 bg-background-50 hover:bg-background-100 transition-colors">
                    <td className="px-4 md:px-6 py-3 font-semibold text-foreground-900">480 kW DC</td>
                    <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹38,15,700</td>
                    <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹25,000</td>
                    <td className="px-4 md:px-6 py-3 text-right text-foreground-700">₹1,51,040</td>
                    <td className="px-4 md:px-6 py-3 text-right font-bold text-primary-600">₹39,91,740</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </FadeUp>

          <FadeUp delay={200}>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-foreground-500">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-truck-line w-4 h-4 flex items-center justify-center text-secondary-500"></i>
                Transportation charges extra as per actuals
              </span>
              <span className="hidden sm:inline text-background-300">|</span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-shield-check-line w-4 h-4 flex items-center justify-center text-accent-500"></i>
                All totals include applicable GST
              </span>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* More Solar Services */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="section-padding">
          <FadeUp>
            <div className="text-center mb-10 md:mb-14">
              <span className="text-sm font-semibold text-accent-600 uppercase tracking-wider">Also Available</span>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900 mt-2">More Solar &amp; Energy Services</h2>
              <p className="text-foreground-600 mt-2 max-w-2xl mx-auto">Specialized solar solutions to complement our core offerings.</p>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto">
            {services.filter(s => ["off-grid", "commercial", "water-pump", "fencing"].includes(s.id)).map((service, i) => (
              <FadeUp key={service.id} delay={i * 100}>
                <div className="flex flex-col md:flex-row bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-300/50 transition-all hover:-translate-y-1 shadow-sm hover:shadow-md">
                  <div className="md:w-2/5 h-48 md:h-auto shrink-0 overflow-hidden">
                    <img src={service.image} alt={service.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5 md:p-6 flex flex-col justify-center">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-3">
                      <i className={`${service.icon} w-5 h-5 flex items-center justify-center`}></i>
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-foreground-900 mb-2">{service.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-3">{service.description}</p>
                    <ul className="space-y-1.5">
                      {service.features.map((f) => (
                        <li key={f} className="flex items-center gap-2 text-sm text-foreground-600">
                          <i className="ri-check-line w-4 h-4 flex items-center justify-center text-accent-500 shrink-0"></i>
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Solar Product Price List */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="section-padding">
          <FadeUp>
            <div className="text-center mb-10 md:mb-14">
              <span className="text-sm font-semibold text-accent-600 uppercase tracking-wider">Pricing</span>
              <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-900 mt-2">Solar Product Price List</h2>
              <p className="text-foreground-600 mt-3 max-w-2xl mx-auto">Transparent pricing across all solar products. Contact us for bulk orders and custom quotes.</p>
            </div>
          </FadeUp>

          <div className="max-w-6xl mx-auto space-y-8 md:space-y-10">
            {/* EPC On-Grid */}
            {(() => {
              const epc = products.find(p => p.id === "epc-on-grid");
              if (!epc) return null;
              return (
                <FadeUp>
                  <div className="bg-background-100 rounded-2xl border border-background-200/70 overflow-hidden shadow-sm">
                    <div className="p-5 md:p-7 border-b border-background-200/70">
                      <div className="flex items-center gap-3 mb-1">
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                          <i className="ri-sun-line w-5 h-5 flex items-center justify-center"></i>
                        </div>
                        <div>
                          <h3 className="font-heading font-bold text-lg md:text-xl text-foreground-900">{epc.category}</h3>
                          {epc.badge && <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium">{epc.badge}</span>}
                        </div>
                      </div>
                      <p className="text-sm text-foreground-600 mt-2 ml-[52px]">{epc.description}</p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-primary-50 border-b border-background-200/70">
                            <th className="text-left px-5 md:px-7 py-3 font-semibold text-foreground-700">Capacity</th>
                            <th className="text-right px-5 md:px-7 py-3 font-semibold text-foreground-700">Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {epc.items.map((item, i) => (
                            <tr key={item.name} className={`border-b border-background-200/70 hover:bg-background-50 transition-colors ${i === epc.items.length - 1 ? 'border-b-0' : ''}`}>
                              <td className="px-5 md:px-7 py-3 text-foreground-700">{item.name}</td>
                              <td className="px-5 md:px-7 py-3 text-right font-semibold text-primary-600">{item.price}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </FadeUp>
              );
            })()}

            {/* DIY On-Grid */}
            {(() => {
              const diy = products.find(p => p.id === "diy-on-grid");
              if (!diy) return null;
              return (
                <FadeUp delay={100}>
                  <div className="bg-background-100 rounded-2xl border border-background-200/70 overflow-hidden shadow-sm">
                    <div className="p-5 md:p-7 border-b border-background-200/70">
                      <div className="flex items-center gap-3 mb-1">
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                          <i className="ri-tools-line w-5 h-5 flex items-center justify-center"></i>
                        </div>
                        <div>
                          <h3 className="font-heading font-bold text-lg md:text-xl text-foreground-900">{diy.category}</h3>
                          {diy.badge && <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 font-medium">{diy.badge}</span>}
                        </div>
                      </div>
                      <p className="text-sm text-foreground-600 mt-2 ml-[52px]">{diy.description}</p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-secondary-50 border-b border-background-200/70">
                            <th className="text-left px-5 md:px-7 py-3 font-semibold text-foreground-700">Capacity</th>
                            <th className="text-right px-5 md:px-7 py-3 font-semibold text-foreground-700">Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {diy.items.map((item, i) => (
                            <tr key={item.name} className={`border-b border-background-200/70 hover:bg-background-50 transition-colors ${i === diy.items.length - 1 ? 'border-b-0' : ''}`}>
                              <td className="px-5 md:px-7 py-3 text-foreground-700">{item.name}</td>
                              <td className="px-5 md:px-7 py-3 text-right font-semibold text-primary-600">{item.price}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </FadeUp>
              );
            })()}

            {/* Solar Water Pumps */}
            {(() => {
              const pumps = products.find(p => p.id === "solar-pumps");
              if (!pumps) return null;
              return (
                <FadeUp delay={150}>
                  <div className="bg-background-100 rounded-2xl border border-background-200/70 overflow-hidden shadow-sm">
                    <div className="p-5 md:p-7 border-b border-background-200/70">
                      <div className="flex items-center gap-3 mb-1">
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                          <i className="ri-water-flash-line w-5 h-5 flex items-center justify-center"></i>
                        </div>
                        <div>
                          <h3 className="font-heading font-bold text-lg md:text-xl text-foreground-900">{pumps.category}</h3>
                          {pumps.badge && <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium">{pumps.badge}</span>}
                        </div>
                      </div>
                      <p className="text-sm text-foreground-600 mt-2 ml-[52px]">{pumps.description}</p>
                    </div>
                    <div className="p-5 md:px-7 md:pb-3 bg-accent-50 border-b border-accent-200/50">
                      <div className="flex items-center gap-2 text-sm">
                        <i className="ri-gift-line w-5 h-5 flex items-center justify-center text-accent-500"></i>
                        <span className="font-semibold text-accent-700">Government Subsidy: {companyInfo.subsidyAmount}</span>
                        <span className="text-accent-600">available under PM Surya Ghar &amp; state schemes</span>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-accent-50 border-b border-background-200/70">
                            <th className="text-left px-5 md:px-7 py-3 font-semibold text-foreground-700">HP</th>
                            <th className="text-right px-5 md:px-7 py-3 font-semibold text-foreground-700">Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pumps.items.map((item, i) => (
                            <tr key={item.name} className={`border-b border-background-200/70 hover:bg-background-50 transition-colors ${i === pumps.items.length - 1 ? 'border-b-0' : ''}`}>
                              <td className="px-5 md:px-7 py-3 text-foreground-700">{item.name}</td>
                              <td className="px-5 md:px-7 py-3 text-right font-semibold text-primary-600">{item.price}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </FadeUp>
              );
            })()}

            {/* Solar Batteries */}
            {(() => {
              const batteries = products.find(p => p.id === "solar-batteries");
              if (!batteries) return null;
              return (
                <FadeUp delay={200}>
                  <div className="bg-background-100 rounded-2xl border border-background-200/70 overflow-hidden shadow-sm">
                    <div className="p-5 md:p-7 border-b border-background-200/70">
                      <div className="flex items-center gap-3 mb-1">
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                          <i className="ri-battery-2-charge-line w-5 h-5 flex items-center justify-center"></i>
                        </div>
                        <div>
                          <h3 className="font-heading font-bold text-lg md:text-xl text-foreground-900">{batteries.category}</h3>
                        </div>
                      </div>
                      <p className="text-sm text-foreground-600 mt-2 ml-[52px]">{batteries.description}</p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-secondary-50 border-b border-background-200/70">
                            <th className="text-left px-5 md:px-7 py-3 font-semibold text-foreground-700">Battery</th>
                            <th className="text-right px-5 md:px-7 py-3 font-semibold text-foreground-700">Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {batteries.items.map((item, i) => (
                            <tr key={item.name} className={`border-b border-background-200/70 hover:bg-background-50 transition-colors ${i === batteries.items.length - 1 ? 'border-b-0' : ''}`}>
                              <td className="px-5 md:px-7 py-3 text-foreground-700">{item.name}</td>
                              <td className="px-5 md:px-7 py-3 text-right font-semibold text-primary-600">{item.price}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </FadeUp>
              );
            })()}

            {/* Terms */}
            <FadeUp delay={250}>
              <div className="max-w-4xl mx-auto p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70">
                <div className="flex items-start gap-3">
                  <i className="ri-information-line w-5 h-5 flex items-center justify-center text-primary-500 mt-0.5 shrink-0"></i>
                  <div>
                    <h3 className="font-heading font-semibold text-base text-foreground-900 mb-2">Terms &amp; Conditions</h3>
                    <ul className="space-y-1.5 text-sm text-foreground-600">
                      <li className="flex items-start gap-2">
                        <span className="text-foreground-400 mt-1">•</span>
                        <span>GST extra where applicable.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-foreground-400 mt-1">•</span>
                        <span>Prices subject to change based on market rates and availability.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-foreground-400 mt-1">•</span>
                        <span>MOQ (Minimum Order Quantity) as per availability.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-foreground-400 mt-1">•</span>
                        <span>EPC pricing includes installation unless stated otherwise.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-foreground-400 mt-1">•</span>
                        <span>Subsidy amount may vary based on government scheme eligibility and capacity.</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </FadeUp>
          </div>

          <FadeUp delay={300}>
            <div className="text-center mt-10">
              <Link to="/products" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-primary-500 text-primary-600 font-medium hover:bg-primary-500 hover:text-background-50 transition-all hover:-translate-y-0.5">
                <i className="ri-price-tag-3-line w-4 h-4 flex items-center justify-center"></i>
                <span>View Full Price List</span>
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Process */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="section-padding">
          <FadeUp>
            <div className="text-center mb-10 md:mb-14">
              <span className="text-sm font-semibold text-accent-600 uppercase tracking-wider">How It Works</span>
              <h2 className="font-heading font-bold text-2xl md:text-4xl text-foreground-900 mt-2">Our 5-Step Process</h2>
            </div>
          </FadeUp>

          <StaggerContainer staggerDelay={120} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 md:gap-3 max-w-6xl mx-auto">
            {[
              { step: "01", title: "Free Consultation", desc: "Discuss your energy needs and budget with our experts." },
              { step: "02", title: "Site Survey", desc: "Our team visits your location for a detailed technical assessment." },
              { step: "03", title: "Custom Design", desc: "We design a system optimized for your roof, usage, and budget." },
              { step: "04", title: "Installation", desc: "Certified technicians install your system with minimal disruption." },
              { step: "05", title: "Commissioning", desc: "System testing, grid connection, and subsidy paperwork filing." },
            ].map((item, i) => (
              <div key={item.step} className="relative text-center p-5 md:p-6">
                <ScaleIn>
                  <div className="w-14 h-14 md:w-16 md:h-16 mx-auto flex items-center justify-center rounded-full bg-primary-500 text-background-50 font-heading font-bold text-xl md:text-2xl mb-4 shadow-lg shadow-primary-500/25">
                    {item.step}
                  </div>
                </ScaleIn>
                <h3 className="font-heading font-semibold text-base text-foreground-900 mb-2">{item.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                {i < 4 && (
                  <div className="hidden md:block absolute top-12 -right-3 w-8 border-t-2 border-dashed border-primary-300"></div>
                )}
              </div>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="section-padding">
          <ScaleIn>
            <div className="max-w-3xl mx-auto text-center p-8 md:p-12 rounded-2xl bg-primary-500 text-background-50 shadow-xl shadow-primary-500/20">
              <h2 className="font-heading font-bold text-2xl md:text-3xl mb-3">Start Your Clean Energy Journey Today</h2>
              <p className="text-base text-primary-100 mb-6">Get a free consultation for solar, drone spraying, or EV charging. Customized proposals for your home, farm, or business.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link to="/contact" className="flex items-center gap-2 px-6 py-3 rounded-full bg-background-50 text-primary-600 font-semibold hover:bg-background-100 transition-all whitespace-nowrap hover:-translate-y-0.5">
                  <i className="ri-calendar-check-line w-4 h-4 flex items-center justify-center"></i>
                  Book Free Survey
                </Link>
                <a href={`tel:${companyInfo.phones[0]}`} className="flex items-center gap-2 px-6 py-3 rounded-full bg-primary-600 text-background-50 font-medium hover:bg-primary-700 transition-all whitespace-nowrap hover:-translate-y-0.5">
                  <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
                  Call Now
                </a>
              </div>
            </div>
          </ScaleIn>
        </div>
      </section>
    </div>
  );
}